import { View } from "react-native";
import React, { useEffect, useRef } from "react";
import { Radio, Text } from "@ui-kitten/components";
import StripePaymentForm, {
  StripeTokenError,
  StripeTokenResponse,
} from "components/StripePaymentForm/StripePaymentForm";
import { useAppSelector } from "hooks/useAppSelector";
import { useMutation } from "@tanstack/react-query";
import { planIdProperty } from "./helpers";
import { createContactFetcher } from "queries/user.queries";
import Button from "components/Button";
import PaymentMethodCard from "screens/Account/components/PaymentMethodCard";
import useAuth from "hooks/useAuth";
import useSignupCharge from "hooks/useSignupCharge";

const StepFour = ({
  handleTokenReceived,
  emailGated,
  disabled,
  beforeButton,
}: {
  handleTokenReceived: (
    error: StripeTokenError | undefined,
    response?: StripeTokenResponse
  ) => void;
  emailGated: boolean;
  disabled?: boolean;
  beforeButton?: React.ReactNode;
}) => {
  const { selectedCmsPlan, signupCharges, quantity } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const {
    isEditingPrimaryUser,
    primaryUser,
    selectedPlanPackage,
    selectedBillingInterval,
  } = useAppSelector((state) => state.buyFlow);
  // Same lookup ShopPhaseTwo uses when it charges the account, so the wallet
  // sheet shows the amount that will actually be charged.
  const planPackageSignupCharge = signupCharges.find(
    (charge) =>
      charge.billingInterval === selectedBillingInterval &&
      charge.planPackageId === selectedCmsPlan?.[planIdProperty]
  );
  const { totalCharge } = useSignupCharge(planPackageSignupCharge?.signupCharge);
  const contactSubmitted = useRef(false); // Ref that keeps track of whether contact has been submitted, to prevent multiple submissions

  const { mutateAsync: createContact } = useMutation(createContactFetcher);
  const auth = useAuth();

  const shouldBill = Number(selectedPlanPackage?.fullPrice) > 0;
  const isLoggedIn = auth.isLoggedIn;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (
          primaryUser.email &&
          contactSubmitted.current === false &&
          !emailGated
        ) {
          contactSubmitted.current = true;
          createContact({
            firstName: primaryUser.firstName,
            lastName: primaryUser.lastName,
            phone: primaryUser.phone,
            email: primaryUser.email,
            source: "SHOP_PAGE_PHASE_TWO",
            planPackageId: selectedCmsPlan![planIdProperty]!,
            buyflowStep: "Payment Information",
          });
        }
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [emailGated, selectedCmsPlan, primaryUser]);

  return (
    <View className="flex-1">
      {shouldBill && (
        <>
          <Text category="h6">Payment</Text>
          <View className="bg-white rounded-xl my-4 shadow-lg font-semibold text-lg">
            <View className="border-b-2 border-b-stone-200">
              <View className="p-4">
                <Radio checked>Credit Card</Radio>
              </View>
            </View>
            <View className="p-4">
              {isLoggedIn ? (
                <>
                  <PaymentMethodCard variant="outline" />
                  <View className="mt-4">
                    {beforeButton}
                    <Button
                      onPress={() => handleTokenReceived(undefined)}
                      disabled={isEditingPrimaryUser || disabled}
                    >
                      Complete Purchase
                    </Button>
                  </View>
                </>
              ) : (
                <StripePaymentForm
                  onTokenReceived={handleTokenReceived}
                  disabled={isEditingPrimaryUser || disabled}
                  beforeButton={beforeButton}
                  walletAmount={
                    planPackageSignupCharge
                      ? Number(totalCharge) * quantity
                      : null
                  }
                />
              )}
            </View>
          </View>
        </>
      )}
      {!shouldBill && (
        <View className=" font-semibold text-lg">
          <View className="p-4">
            {beforeButton}
            <Button
              onPress={() => handleTokenReceived(undefined)}
              disabled={isEditingPrimaryUser || disabled}
            >
              {isLoggedIn ? "Complete Purchase" : "Create My Account"}
            </Button>
          </View>
        </View>
      )}
    </View>
  );
};

export default StepFour;
